import { diffDays } from './dates';
import type { LeadTimeEstimate, LeadTimeSample, PlanningItem, PlanningSupplier } from './types';

/** Only the most recent receipts describe how the vendor performs today. */
const RECENT_SAMPLE_LIMIT = 8;

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

/**
 * What lead time to plan with for one purchased part.
 *
 *   typical — what usually happens: the median of recent receipts
 *             (ordered → first received), else the vendor's quote, else
 *             the catalog's worst-case figure.
 *   worst   — what to protect against: the longest of the observed
 *             maximum, the quote and the catalog figure.
 *
 * Samples for the chosen vendor win; if there are none, receipts from any
 * vendor of the same part are used (the part is the same, the route is
 * probably similar). Negative or absurd samples (received before ordered,
 * or > 2 years) are ignored as data-entry errors.
 */
export function estimateLeadTime(
  item: PlanningItem,
  supplier: PlanningSupplier | null,
  samples: LeadTimeSample[],
): LeadTimeEstimate {
  const valid = samples
    .map((s) => ({ ...s, days: diffDays(s.orderedAt, s.receivedAt) }))
    .filter((s) => s.days >= 0 && s.days <= 730);

  const forVendor = supplier ? valid.filter((s) => s.vendorId === supplier.vendorId) : [];
  const pool = (forVendor.length ? forVendor : valid)
    .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
    .slice(0, RECENT_SAMPLE_LIMIT);

  const observed = pool.map((s) => s.days);
  const observedMedian = observed.length ? median(observed) : null;
  const observedMax = observed.length ? Math.max(...observed) : null;
  const observedMin = observed.length ? Math.min(...observed) : null;

  const quoted = supplier?.leadTimeDays ?? null;
  const catalog = item.leadTimeWorstCaseDays ?? null;

  const typical = observedMedian ?? quoted ?? catalog;
  const worstCandidates = [observedMax, quoted, catalog].filter((v): v is number => v != null);
  const worst = worstCandidates.length ? Math.max(...worstCandidates) : null;

  const source: LeadTimeEstimate['source'] =
    observed.length > 0 ? 'observed' : quoted != null ? 'quoted' : catalog != null ? 'catalog' : 'none';

  return {
    typicalDays: typical,
    worstDays: worst,
    source,
    observedSamples: observed.length,
    observedMedianDays: observedMedian,
    observedMaxDays: observedMax,
    observedMinDays: observedMin,
    observedLatestReceipt: pool[0]?.receivedAt ?? null,
    quotedDays: quoted,
    catalogWorstDays: catalog,
  };
}

export function describeLeadTime(estimate: LeadTimeEstimate): string {
  if (estimate.typicalDays == null) return 'No lead time on file';
  const parts: string[] = [];
  if (estimate.source === 'observed') {
    parts.push(
      `${estimate.typicalDays}d typical from ${estimate.observedSamples} receipt${
        estimate.observedSamples === 1 ? '' : 's'
      }`,
    );
  } else if (estimate.source === 'quoted') {
    parts.push(`${estimate.typicalDays}d quoted by vendor`);
  } else {
    parts.push(`${estimate.typicalDays}d from catalog`);
  }
  if (estimate.worstDays != null && estimate.worstDays !== estimate.typicalDays) {
    parts.push(`${estimate.worstDays}d worst case`);
  }
  return parts.join(' · ');
}
